import React, { useEffect, useState } from 'react';
import {
   createLDReactProvider,
   useInitializationStatus,
   useLDClient,
} from '@launchdarkly/react-sdk';

import FeatureFlagContext from './feature-flag-context';
import FeatureFlagDetailsContext, {
   FEATURE_FLAG_DETAIL_DEFAULTS,
} from './feature-flag-details-context';
import { FEATURE_FLAG_DEFAULTS, FEATURE_FLAG_KEYS } from './feature-flags';

const clientSideId = process.env.GATSBY_LAUNCHDARKLY_CLIENT_SIDE_ID;

const flagKeys = Object.values(FEATURE_FLAG_KEYS);

const getLaunchDarklyFlagValues = (client) =>
   flagKeys.reduce(
      (flags, flagKey) => ({
         ...flags,
         [flagKey]: client.variation(flagKey, FEATURE_FLAG_DEFAULTS[flagKey]),
      }),
      { ...FEATURE_FLAG_DEFAULTS }
   );

// variationIndex comes back null whenever the SDK served the fallback we
// passed in, which is exactly when we want diagnostics to say "unavailable"
// rather than inventing a bucket.
const getLaunchDarklyFlagDetails = (client) =>
   flagKeys.reduce((details, flagKey) => {
      const { value, variationIndex } = client.variationDetail(
         flagKey,
         FEATURE_FLAG_DEFAULTS[flagKey]
      );
      return { ...details, [flagKey]: { value, variationIndex: variationIndex ?? null } };
   }, {});

const LaunchDarklyFlagBridge = ({ children }) => {
   const client = useLDClient();
   const initialization = useInitializationStatus();
   const [flags, setFlags] = useState(FEATURE_FLAG_DEFAULTS);
   const [flagDetails, setFlagDetails] = useState(FEATURE_FLAG_DETAIL_DEFAULTS);

   useEffect(() => {
      if (!client || !client.isReady()) return undefined;

      const updateFlags = () => {
         setFlags(getLaunchDarklyFlagValues(client));
         setFlagDetails(getLaunchDarklyFlagDetails(client));
      };

      updateFlags();
      flagKeys.forEach((flagKey) => client.on(`change:${flagKey}`, updateFlags));

      return () => {
         flagKeys.forEach((flagKey) => client.off(`change:${flagKey}`, updateFlags));
      };
   }, [client, initialization.status]);

   return (
      <FeatureFlagContext.Provider value={flags}>
         <FeatureFlagDetailsContext.Provider value={flagDetails}>
            {children}
         </FeatureFlagDetailsContext.Provider>
      </FeatureFlagContext.Provider>
   );
};

const LaunchDarklyProvider =
   typeof window !== 'undefined' && clientSideId
      ? createLDReactProvider(
           clientSideId,
           {
              kind: 'user',
              anonymous: true,
           },
           {
              ldOptions: {
                 streaming: true,
              },
           }
        )
      : null;

export const wrapWithLaunchDarkly = ({ element }) => {
   if (!LaunchDarklyProvider) {
      return (
         <FeatureFlagContext.Provider value={FEATURE_FLAG_DEFAULTS}>
            <FeatureFlagDetailsContext.Provider value={FEATURE_FLAG_DETAIL_DEFAULTS}>
               {element}
            </FeatureFlagDetailsContext.Provider>
         </FeatureFlagContext.Provider>
      );
   }

   return (
      <LaunchDarklyProvider>
         <LaunchDarklyFlagBridge>{element}</LaunchDarklyFlagBridge>
      </LaunchDarklyProvider>
   );
};
